import { useSoyuzData } from '@spotahome/soyuz/client';

import {
  ToastContainer,
  ThemeProvider,
  CookiesProvider,
  FooterSimplified,
  GoogleOneTap,
  UserInfoProvider,
  TenantTypeProvider,
  DeviceProvider,
  AppBanner,
  CitiesProvider,
  DateRangeHandler,
  LoginSignupModalWrapper
} from '@spotahome/ui-library';
import { useCoreWebVitals } from '@spotahome/ui-library/src/hooks/useCoreWebVitals';
import usePageLoadTime from '@spotahome/ui-library/src/hooks/usePageLoadTime';
import ZohoChatbot from '@spotahome/ui-library/src/tenant/components/ZohoChatbot/ZohoChatbot';
import InformationModal from '@spotahome/ui-library/src/tenant/components/InformationModal';

import {
  CustomDimensionsManager,
  constants
} from '@spotahome/ecommerce-tracking';

import HeaderContainer from '@spotahome/marketplace-common/src/components/HeaderContainer';

import InformationToastWrapper from '@spotahome/marketplace-common/src/components/InformationToast/index';
import OptInModalWrapper from '@spotahome/marketplace-common/src/components/OptInModalWrapper';
import SahContainer from '@spotahome/marketplace-common/src/components/SahContainer';

import {
  getListing,
  getLastUnpublishedDate
} from '@spotahome/marketplace-common/src/graphql/queries/listing';
import { getCurrentCityQuery } from '@spotahome/marketplace-common/src/graphql/queries/currentCity';
import getLandlordInformationQuery from '@spotahome/marketplace-common/src/graphql/queries/landlordInformation';
import { createChatChannel } from '@spotahome/marketplace-common/src/graphql/mutations/chat';

import { FavoriteProvider } from '@spotahome/ui-library/src/tenant/context/FavoriteContext';
import { ShareProvider } from '@spotahome/ui-library/src/tenant/context/ShareContext';

import { ListingFormatter } from '@spotahome/ui-library/src/tenant/utils/images';
import GracePeriodCancellationModal from '@spotahome/ui-library/src/tenant/components/GracePeriodCancellationModal';

import { buildHeadersData, getHeadersEmptyResponse } from './builders/headers';
import { getAppEmptyResponse, buildAppData } from './builders/app';

import { InstantBookingProvider } from './context/InstantBookingContext';
import LandlordInformationProvider from './context/LandlordInformationContext';

import ShowRentableUnit from './components/ShowRentableUnit';
import RentableUnitNotFound from './components/RentableUnitNotFound';

import { ACTION_CREATE_CHAT, BOOKING_STATUSES } from './constants';

import './main.scss';
import { isValidDate } from './utils/isValidDate';
import { getBookingStatus } from './utils/getBookingStatus';

const SIX_MONTHS_IN_DAYS = 6 * 30;

const getEmptyResponse = city => ({
  headers: getHeadersEmptyResponse(city),
  data: getAppEmptyResponse(city)
});

const getValidUrl = ({
  getPagePath,
  currentRoute,
  locale,
  query,
  listingId,
  listing
}) => {
  const rentableUnitParams = {
    id: listingId,
    city: listing.area,
    property: listing.ptype.title
  };

  const queryCopy = { ...query };

  if (query?.['move-in'] && query?.['move-out']) {
    const moveIn = isValidDate(query['move-in']) ? query['move-in'] : '';
    const moveOut = isValidDate(query['move-out']) ? query['move-out'] : '';

    const bookingStatus = getBookingStatus(moveIn, moveOut, listing);
    const notValidDates =
      bookingStatus === BOOKING_STATUSES.NOT_AVAILABLE_VALIDATION;

    queryCopy['move-in'] = notValidDates ? '' : moveIn;
    queryCopy['move-out'] = notValidDates ? '' : moveOut;
  }

  return getPagePath(currentRoute, {
    locale,
    query: queryCopy,
    params: rentableUnitParams
  });
};

const ShowRentableUnitPage = () => {
  const { [ShowRentableUnitPage.NAME]: requestedData } = useSoyuzData();

  const {
    listing,
    areaSlug,
    isFavorite,
    isRoomsAggregator,
    seo,
    favorites,
    moveIn,
    moveOut,
    isLogged,
    userId,
    deviceType,
    deviceOS,
    deviceViewType,
    initialCookie,
    cityInfo,
    internalLinking,
    landlordHomecards,
    landlordInformation,
    error
  } = requestedData;

  const listingIsFavorite = isFavorite === 'true' || isFavorite === true;

  const listingCanonicalUrl = seo ? seo.canonical : '';

  useCoreWebVitals();
  usePageLoadTime();

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <SahContainer>
          <AppBanner source="listing" deviceType={deviceType} />
          <ToastContainer />
          <ZohoChatbot />
          <InformationToastWrapper city={areaSlug} />
          <CitiesProvider initialCityInfo={cityInfo} skipAllCities>
            <UserInfoProvider>
              <TenantTypeProvider>
                <DeviceProvider deviceType={deviceType} deviceOS={deviceOS}>
                  {error && error === 'notFound' ? (
                    <FavoriteProvider
                      listingId={requestedData.listingId}
                      cityId={cityInfo?.id}
                      propertyType={requestedData.propertyType}
                      isFavorite={listingIsFavorite}
                      favorites={favorites}
                    >
                      <RentableUnitNotFound
                        deviceType={deviceType}
                        cityInfo={cityInfo}
                      />
                    </FavoriteProvider>
                  ) : (
                    <LandlordInformationProvider
                      landlordInformation={landlordInformation}
                    >
                      <ShareProvider
                        listingId={listing.id}
                        sharesCount={listing.statistics.shared}
                      >
                        <FavoriteProvider
                          listingId={listing.id}
                          cityId={listing.cityId}
                          propertyType={listing.type}
                          price={listing.pricing.price}
                          isFavorite={listingIsFavorite}
                          favorites={favorites}
                          favoritesCount={listing.statistics.favorite}
                        >
                          <InstantBookingProvider {...listing.instantBooking}>
                            <div className="listing-header">
                              <HeaderContainer />
                            </div>
                            <GracePeriodCancellationModal />
                            <InformationModal />
                            {!isLogged && <GoogleOneTap />}
                            <ShowRentableUnit
                              moveIn={moveIn}
                              moveOut={moveOut}
                              areaSlug={areaSlug}
                              listing={listing}
                              isRoomsAggregator={isRoomsAggregator}
                              listingCanonicalUrl={listingCanonicalUrl}
                              isLogged={isLogged}
                              userId={userId}
                              deviceType={deviceType}
                              deviceOS={deviceOS}
                              internalLinking={internalLinking}
                              landlordHomecards={landlordHomecards}
                            />
                          </InstantBookingProvider>
                        </FavoriteProvider>
                      </ShareProvider>
                    </LandlordInformationProvider>
                  )}
                </DeviceProvider>
              </TenantTypeProvider>
              <LoginSignupModalWrapper deviceViewType={deviceViewType} />
            </UserInfoProvider>
          </CitiesProvider>
          <OptInModalWrapper />
          <FooterSimplified />
        </SahContainer>
      </ThemeProvider>
    </CookiesProvider>
  );
};

ShowRentableUnitPage.NAME = 'ShowRentableUnit';

ShowRentableUnitPage.requestAllData = async (
  req,
  _,
  { res, graphqlClient: graphqlClientRequest, clientConfig }
) => {
  const { params, locale, query, currentRoute, originalUrl, userId } = req;
  const { id: rentableUnitId, property } = params;

  if (query.action === ACTION_CREATE_CHAT && userId) {
    try {
      const response = await createChatChannel(graphqlClientRequest)({
        listingId: rentableUnitId
      });
      const channelId = response?.data?.chatCreateChannel?.channelId;
      const chatUrl = req.getPagePath('tenant-messages', {
        query: { id: channelId }
      });
      res.redirect(302, chatUrl);
      return null;
    } catch (err) {
      req.logger.error(
        `Error creating the chat for listing ${rentableUnitId}`,
        { err }
      );
    }
  }

  const listingResponse = await getListing(graphqlClientRequest)(
    rentableUnitId,
    locale || 'en'
  );

  let currentCity = null;

  try {
    const currentCityResponse = await getCurrentCityQuery(graphqlClientRequest)(
      params.city,
      req.locale
    );
    currentCity = currentCityResponse.data.currentCity;
  } catch (error) {
    req.errorTracker.warning({
      error,
      source: `ListingPage-RequestData-GraphQL-Error-getCurrentCity`,
      locale,
      rentableUnitId,
      cityId: params.city
    });
  }

  const LISTING_NOT_FOUND_PATTERN = /\b(Request failed with status code 404)\b/;

  const isListingNotFound = listingResponse?.errors?.some(error =>
    LISTING_NOT_FOUND_PATTERN.test(error?.message)
  );

  if (listingResponse.errors?.length && !isListingNotFound) {
    listingResponse.errors.forEach(error => {
      req.errorTracker.warning({
        error,
        source: `ListingPage-RequestData-Error`,
        locale,
        rentableUnitId
      });
    });
  }

  if (listingResponse.graphQLErrors?.length && !isListingNotFound) {
    listingResponse.graphQLErrors.forEach(error => {
      req.errorTracker.warning({
        error,
        source: `ListingPage-RequestData-GraphQL-Error`,
        locale,
        rentableUnitId
      });
    });
  }

  const rentableUnitData = listingResponse.data.listing;
  const { favorites, listingStatistics, isFavorite } = listingResponse.data;
  if (
    !rentableUnitData ||
    !rentableUnitData?.listing ||
    !rentableUnitData?.cityInfo
  ) {
    let status = 404;

    let lastUnpublishedDateResponse = null;
    try {
      lastUnpublishedDateResponse = await getLastUnpublishedDate(
        graphqlClientRequest
      )(rentableUnitId);
    } catch (error) {
      req.errorTracker.warning({
        error,
        source: `ListingPage-RequestData-GraphQL-Error-getLastUnpublishedDate`,
        locale,
        rentableUnitId
      });
    }

    const lastUnpublishedDateValue =
      lastUnpublishedDateResponse?.data?.ad?.lastUnpublishedDate;

    status =
      lastUnpublishedDateValue &&
      DateRangeHandler.getDaysDiff(lastUnpublishedDateValue, new Date()) >
        SIX_MONTHS_IN_DAYS
        ? 410
        : 404;

    res.status(status);

    return {
      data: {
        error: 'notFound',
        deviceType: req.device.type,
        deviceViewType: req.device.viewType,
        initialCookie: req.headers.cookie,
        isLogged: !!req.userId,
        cityInfo: currentCity,
        listingId: rentableUnitId,
        propertyType: property,
        favorites,
        isFavorite
      }
    };
  }

  let landlordInformationResponse = null;
  try {
    landlordInformationResponse = await getLandlordInformationQuery(
      graphqlClientRequest
    )(listingResponse.data.listing.listing.landlordId);
  } catch (error) {
    req.errorTracker.warning({
      error,
      source: `ListingPage-RequestData-GraphQL-Error-getLandlordInformation`,
      locale,
      rentableUnitId
    });
  }

  try {
    const { cityInfo, listingInternalLinking, listing, landlordHomecards } =
      rentableUnitData;
    const { cityName, id: city } = cityInfo;

    if (listing) {
      const validUrl = getValidUrl({
        getPagePath: req.getPagePath,
        currentRoute,
        locale,
        query,
        listing,
        listingId: rentableUnitId
      });

      if (originalUrl !== validUrl) {
        res.redirect(302, validUrl);
        return null;
      }
    }

    const cdManager = CustomDimensionsManager();

    cdManager.setSearchCommonCustomDimensions({
      userId,
      city,
      locale,
      pageType: constants.TRACKING_PAGE_TYPES.PRODUCT_PAGE
    });

    return {
      headers: buildHeadersData({
        city,
        cityName,
        clientConfig,
        rentableUnitData,
        req,
        dataLayerScript: cdManager.flush(),
        imagesToBePreloaded: [
          ListingFormatter.buildPhotoUrl(
            listing.photos.photoList[0].id,
            listing.photos.photoList[0].verified
          )
        ]
      }),
      data: {
        ...buildAppData({
          isFavorite,
          internalLinking: listingInternalLinking,
          query,
          rentableUnitData,
          listingStatistics
        }),
        favorites,
        landlordInformation:
          landlordInformationResponse?.data?.landlordInformation ?? null,
        isLogged: listingResponse.data && listingResponse.data.isLogged,
        cityInfo,
        isRoomsAggregator: rentableUnitData.isRoomsAggregator,
        locale,
        userId,
        deviceType: req.device.type,
        deviceOS: req.device?.info?.os?.name,
        deviceViewType: req.device.viewType,
        initialCookie: req.headers.cookie,
        landlordHomecards,
        propertyType: property
      }
    };
  } catch (error) {
    req.errorTracker.error({
      error,
      source: `${ShowRentableUnitPage.NAME}-EmptyResponse`,
      rentableUnitId,
      locale,
      cityId: params.city
    });
    return getEmptyResponse(params.city);
  }
};

export default ShowRentableUnitPage;
